import React, {createContext, PropsWithChildren, useCallback, useContext, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {isEqual, isFunction} from "lodash";
import {useHistory} from "react-router-dom";
import * as actions from '../_redux/aiReportsActions';
import {generateExcelFile} from "../../_common/utils/excel";
import {aiReportHeader} from "../../_common/constants/exportedFilesMainHeaders";
import {showGlobalMessage} from "../../_common/actions/globalMessage/showGlobalMessage";
import {messages as M} from "../../../../_metronic/i18n/messages";

type AiReportsContext = {
  canAnswer: boolean;
  reason: string;
  reportColumns: ReportColumn[];
  loading: boolean;
  reportId: string;
  exporting: boolean;
  totalCount: number;
  entities: never[] | null;
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => void;
  handleExport: () => Promise<void>
  saveQuery: () => Promise<void>
  isSaving: boolean,
}
type ReportColumn = {
  accessor: string;
  Header: string;
};

export interface QueryParams {
  [key: string]: string | number | undefined;
}

type RootState = {
  aiReports: {
    canAnswer: boolean;
    reason: string;
    columns: { field: string; title: string; }[];
    listLoading: boolean;
    totalCount: number;
    entities: never[];
    reportId: string;
  };
};

interface AiReportsProviderProps extends PropsWithChildren<{}> {
}

const AiReportsContext = createContext<AiReportsContext>({
  canAnswer: false,
  reason: '',
  reportColumns: [],
  loading: false,
  reportId: '',
  exporting: false,
  totalCount: 0,
  entities: null,
  queryParams: {},
  setQueryParamsBase: () => {
  },
  setQueryParams: () => {
  },
  handleExport: async () => {
  },
  saveQuery: async () => {
  },
  isSaving: false,
});

export function useAiReportsContext() {
  return useContext(AiReportsContext);
}

export function AiReportsProvider({children}: AiReportsProviderProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});
  const setQueryParams = useCallback(
    (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => {
      setQueryParamsBase(prevQueryParams => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }

        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }

        for (let key in nextQueryParams) {
          if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) {
            delete nextQueryParams[key];
          }
        }

        const path = history.location.pathname;
        const newQueryParams = new URLSearchParams(nextQueryParams as Record<string, string>);
        history.replace(`${path}?${newQueryParams.toString()}`);

        return nextQueryParams;
      });
    },
    [history]
  );
  const {canAnswer, reason, columns, listLoading, totalCount, entities, reportId} = useSelector(
    (state: RootState) => ({
      canAnswer: state.aiReports.canAnswer,
      reason: state.aiReports.reason,
      columns: state.aiReports.columns,
      listLoading: state.aiReports.listLoading,
      totalCount: state.aiReports.totalCount,
      entities: state.aiReports.entities,
      reportId: state.aiReports.reportId,
    }),
    shallowEqual
  );
  const mappedColumns = Array.isArray(columns)
    ? columns.map(item => {
      return {
        accessor: item?.field ?? '',
        Header: item?.title ?? '',
      }
    })
    : [];

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await actions.getReportListForExcel(reportId);
      const data = prepareDataForExcel(result?.list ?? [], result?.columns ?? []);
      generateExcelFile(
        data,
        `AI_report_${new Date().toLocaleDateString('en-GB').replace(/\//g, '.')}.xlsx`,
        aiReportHeader
      )
    } catch (e: unknown) {
      console.error(`Error: + ${e}`);
    } finally {
      setExporting(false);
    }
  };
  const prepareDataForExcel = (
    list: Record<string, unknown>[],
    columns: { field: string; title: string; }[]
  ): Record<string, unknown>[] => {
    return list.map(item => {
      const row: Record<string, unknown> = {};
      const mappedColumns = Array.isArray(columns)
        ? columns.map(item => {
          return {
            accessor: item?.field ?? '',
            Header: item?.title ?? '',
          }
        })
        : [];

      mappedColumns.forEach(column => {
        const key = column.accessor;
        let value = item[key];

        if (
          typeof value === 'string' &&
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
          !Number.isNaN(Date.parse(value))
        ) {
          value = new Date(value);
        }

        row[column.Header ?? key] = value;
      });

      return row;
    });
  };

  const saveQuery = async () => {
    setIsSaving(true);
    try {
      const errorCode = await actions.saveQuery(reportId);
      if (errorCode === 0) {
        dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      }
    } catch (e: unknown) {
      console.error(`Error + ${e}`)
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AiReportsContext.Provider value={{
      canAnswer,
      reason,
      reportColumns: mappedColumns,
      loading: listLoading,
      exporting,
      totalCount,
      entities,
      reportId,
      queryParams,
      setQueryParamsBase,
      setQueryParams,
      handleExport,
      saveQuery,
      isSaving
    }}>
      {children}
    </AiReportsContext.Provider>
  );
}